/**
 * Mines and proximity posts (02 §9.2). A mine goes off under the ranger's feet, or under a
 * minitank's tracks; the garrison's own men know where they are laid and never set one off.
 * Clearing by blast is `blast.ts`.
 */

import { type Ctx, isAlive } from '../ctx';
import { explode } from '../damage';
import { destroyOutright } from '../enemies/specials/kinds';
import { segmentHit } from '../statics';
import { CARBINE, ENEMY, HAZARDS, SPECIALS, VEHICLES } from '../tuning';
import { DT, type Mine } from '../types';
import { rangerOnFoot, within } from './terrain';

function detonate(ctx: Ctx, m: Mine): void {
  m.live = false;
  ctx.events.push({ type: 'mine', x: m.x, z: m.z });
  // Scaled so that a man standing on it is inside the two-wound core (02 §5): downed if that makes three.
  explode(ctx, m.x, m.z, HAZARDS.mineBlast, 'grenade', false);
}

export function stepMines(ctx: Ctx): void {
  const w = ctx.world;
  const r = w.ranger;
  const onFoot = rangerOnFoot(w);
  // R4: a lorry's wheels set one off exactly as a minitank's tracks do - mines are one of the three things
  // that open armour (the others being a LAW and a time bomb).
  const tanks = w.enemies.filter((e) => (e.kind === 'minitank' || e.vehicle !== undefined) && isAlive(e));
  for (const m of w.hazards.mines) {
    if (!m.live) continue;
    if (onFoot && within(m.x, m.z, r.x, r.z, HAZARDS.mineTrigger)) {
      detonate(ctx, m);
      continue;
    }
    for (const t of tanks) {
      const reach = t.vehicle !== undefined ? VEHICLES.mineTrigger : SPECIALS.tankMineTrigger;
      if (!isAlive(t) || !within(m.x, m.z, t.x, t.z, reach)) continue;
      // The machine has no idea where the mines are: luring it across a field is the cheap answer to it.
      detonate(ctx, m);
      destroyOutright(ctx, t, true);
      break;
    }
  }
}

/**
 * Is any round about to strike this post? Bullets are resolved after the hazards in a tick,
 * and leave nothing behind to ask afterwards, so their next step is looked at ahead of time.
 */
function shotAt(ctx: Ctx, propId: number): boolean {
  if (ctx.world.bullets.length === 0) return false;
  const posts = ctx.statics.colliders.filter((c) => c.propId === propId);
  for (const b of ctx.world.bullets) {
    const step = Math.min(b.range, (b.fromPlayer ? CARBINE.speed : ENEMY.bulletSpeed) * DT);
    for (const c of posts) {
      if (b.ignoreProp !== propId && segmentHit(c, b.x, b.z, b.x + b.dirX * step, b.z + b.dirZ * step)) return true;
    }
  }
  return false;
}

/** A bomb on a post: goes off when the ranger comes within its trigger, or when anyone shoots it. */
export function stepProximityPosts(ctx: Ctx): void {
  const w = ctx.world;
  const r = w.ranger;
  const onFoot = rangerOnFoot(w);
  for (const def of ctx.statics.def.props) {
    if (def.kind !== 'proximityPost') continue;
    const state = w.props.find((p) => p.id === def.id);
    if (!state || state.destroyed) continue;
    const near = onFoot && within(def.x, def.z, r.x, r.z, HAZARDS.proximityTrigger);
    if (!near && !shotAt(ctx, def.id)) continue;
    // Marked destroyed first, so the blast below does not also queue the post's delayed cook-off.
    state.destroyed = true;
    ctx.staticsDirty = true;
    ctx.events.push({ type: 'propDestroyed', id: def.id });
    explode(ctx, def.x, def.z, HAZARDS.proximityBlast, 'heavy', false);
  }
}
